import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CButton, CFormInput, CFormLabel, CRow, CCol, CBadge } from "@coreui/react";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faTrash, faEye, faStar } from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import TNTable from "@/components/TNTable";
import CustomDropdown from "@/components/CustomDropdown";
import { useTranslationContext } from "@/context/TranslationContext";
import { review as reviewTranslations } from "@/constants/translation/review.translation";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { useListReview, useDeleteReview } from "@/hooks/useReview";
import { useListFreelancerUser } from "@/hooks/useFreelancerUser";
import { useListUser } from "@/hooks/useUserMaster";
import { dateTimeFormat } from "@/utils/dateFormate";
import constants from "@/libs/constant";
import { review as reviewRoutes } from "@/libs/routes";

const reviewT = reviewTranslations.list;
const { pagination: paginationConstants } = constants;

const filterInputStyle = {
  backgroundColor: "#212631",
  borderColor: "#212631",
  color: "white",
};

const TruncateCell = ({ text, className = "reviews-cell-text" }) => (
  <span className={className} title={text}>
    {text}
  </span>
);

const MAX_COMMENT_LENGTH = 80;

const CommentCell = ({ text }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) {
    return <span style={{ wordBreak: "break-word", whiteSpace: "normal", lineHeight: "1.3" }}>{text}</span>;
  }

  const isLong = text.length > MAX_COMMENT_LENGTH;

  return (
    <span style={{ wordBreak: "break-word", whiteSpace: "normal", lineHeight: "1.3" }}>
      {expanded || !isLong ? text : `${text.slice(0, MAX_COMMENT_LENGTH)}...`}
      {isLong && (
        <>
          {" "}
          <button
            onClick={() => setExpanded((prev) => !prev)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "var(--cui-primary, #0d6efd)",
              fontSize: "inherit",
              fontWeight: "600",
              textDecoration: "underline",
            }}
          >
            {expanded ? "Read less" : "Read more"}
          </button>
        </>
      )}
    </span>
  );
};

const ReviewList = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [freelancerUuid, setFreelancerUuid] = useState("");
  const [reviewerUuid, setReviewerUuid] = useState("");
  const [rating, setRating] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tableKey, setTableKey] = useState(0);

  const searchTimeout = useRef(null);
  const dateTimeout = useRef(null);
  const isFiltering = useRef(false);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      if (dateTimeout.current) clearTimeout(dateTimeout.current);
    };
  }, []);

  const buildFilters = useCallback(
    () => ({
      ...(freelancerUuid && { freelancer_uuid: freelancerUuid }),
      ...(reviewerUuid && { reviewer_uuid: reviewerUuid }),
      ...(rating && { rating }),
      ...(startDate && { start_date: startDate }),
      ...(endDate && { end_date: endDate }),
    }),
    [freelancerUuid, reviewerUuid, rating, startDate, endDate]
  );

  const { data: response, isFetching } = useListReview(
    {
      page: currentPage,
      limit: paginationConstants.defaultPageSize,
      ...(searchText && { search: searchText }),
      ...buildFilters(),
    },
    null,
    () => toast.error(t(reviewT.messages.fetch_failed))
  );

  const { data: freelancerListData } = useListFreelancerUser({ limit: 100 });
  const { data: userListData } = useListUser({ limit: 100 });

  const { mutate: doDeleteReview } = useDeleteReview(() => {
    toast.success(t(reviewT.messages.delete_success));
  });

  useEffect(() => {
    setLoading(isFetching);

    if (!response) return;

    const list = response?.data?.reviews || [];
    setReviews(list);

    const paginationData = response?.data?.pagination;
    if (paginationData) {
      const perPage = parseInt(paginationData.limit, 10) || paginationConstants.defaultPageSize;
      const total = parseInt(paginationData.total, 10) || 0;

      setPagination({
        total,
        per_page: perPage,
        count: total,
        page: parseInt(paginationData.page, 10) || currentPage,
        total_pages: parseInt(paginationData.total_pages, 10) || Math.ceil(total / perPage) || 1,
      });
    } else {
      setPagination(null);
    }
  }, [response, isFetching, currentPage]);

  const freelancerOptions = useMemo(() => {
    const options = [{ value: "", label: t(reviewT.freelancer_filter_placeholder) }];
    (freelancerListData?.data?.freelancerUsers || []).forEach((freelancer) => {
      options.push({ value: freelancer.uuid, label: freelancer.businessName });
    });
    return options;
  }, [freelancerListData, t]);

  const reviewerOptions = useMemo(() => {
    const options = [{ value: "", label: t(reviewT.reviewer_filter_placeholder) }];
    (userListData?.data?.users || []).forEach((user) => {
      options.push({ value: user.uuid, label: user.full_name || user.fullName || user.user_name });
    });
    return options;
  }, [userListData, t]);

  const ratingOptions = useMemo(
    () =>
      constants.status.reviewRatingOptions.map((option) =>
        option.value === "" ? { ...option, label: t(option.label) } : option
      ),
    [t]
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setTempSearchText(value);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(() => {
      isFiltering.current = true;
      setSearchText(value);
      setCurrentPage(1);
      setTableKey((prev) => prev + 1);
      setTimeout(() => {
        isFiltering.current = false;
      }, 200);
    }, 500);
  };

  const handleDateChange = (dateType, value) => {
    if (dateTimeout.current) clearTimeout(dateTimeout.current);

    dateTimeout.current = setTimeout(() => {
      isFiltering.current = true;

      if (dateType === "start") {
        setStartDate(value);
        if (endDate && endDate < value) setEndDate(value);
      } else if (value < startDate) {
        setEndDate(startDate);
      } else {
        setEndDate(value);
      }

      setCurrentPage(1);
      setTableKey((prev) => prev + 1);
      setTimeout(() => {
        isFiltering.current = false;
      }, 200);
    }, 300);
  };

  const handleFilterChange = (setter) => (e) => {
    isFiltering.current = true;
    setter(e.target.value);
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);
    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const handleClearFilter = () => {
    isFiltering.current = true;

    setTempSearchText("");
    setSearchText("");
    setFreelancerUuid("");
    setReviewerUuid("");
    setRating("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const handleView = (review) => {
    navigate(reviewRoutes.view.replace(":id", review.uuid));
  };

  const handleDelete = (review) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(reviewT.delete_popup.heading)}</h2>
            <p className="text-center mb-2">{t(reviewT.delete_popup.message)}</p>
            <p className="text-center mb-3">{t(reviewT.delete_popup.message_2)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doDeleteReview(review.uuid);
                  onClose();
                }}
              >
                {t(reviewT.delete_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(reviewT.delete_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const columns = useMemo(
    () => [
      {
        id: "review_date",
        header: t(reviewT.table.review_date_col_heading),
        accessorKey: "review_date",
        cell: ({ row }) => (
          <TruncateCell text={dateTimeFormat(row.original.review_date) || t(reviewT.defaults.na)} />
        ),
      },
      {
        id: "reviewer_name",
        header: t(reviewT.table.reviewer_name_col_heading),
        accessorKey: "reviewer_name",
        cell: ({ row }) => <TruncateCell text={row.original.reviewer_name || t(reviewT.defaults.na)} />,
      },
      {
        id: "freelancer_name",
        header: t(reviewT.table.freelancer_name_col_heading),
        accessorKey: "freelancer_name",
        cell: ({ row }) => <TruncateCell text={row.original.freelancer_name || t(reviewT.defaults.na)} />,
      },
      {
        id: "rating",
        header: t(reviewT.table.rating_col_heading),
        accessorKey: "rating",
        cell: ({ row }) => (
          <span className="d-flex align-items-center gap-1">
            <FontAwesomeIcon icon={faStar} className="text-warning" />
            {row.original.rating ?? t(reviewT.defaults.na)}
          </span>
        ),
      },
      {
        id: "comment",
        header: t(reviewT.table.comment_col_heading),
        accessorKey: "comment",
        cell: ({ row }) => <CommentCell text={row.original.comment || t(reviewT.defaults.na)} />,
      },
      {
        id: "has_reply",
        header: t(reviewT.table.reply_col_heading),
        accessorKey: "has_reply",
        cell: ({ row }) => (
          <CBadge color={row.original.has_reply ? "success" : "secondary"}>
            {row.original.has_reply ? t(reviewT.reply_yes) : t(reviewT.reply_no)}
          </CBadge>
        ),
      },
      {
        id: "actions",
        header: t(reviewT.table.actions_col_heading),
        accessorKey: "actions",
        cell: ({ row }) => {
          const reviewRow = row.original;
          return (
            <div className="d-flex gap-2">
              <OverlayTrigger placement="top" overlay={<Tooltip id={`view-${reviewRow.id}`}>{t(reviewT.action_tooltip.view)}</Tooltip>}>
                <CButton
                  size="sm"
                  style={{ backgroundColor: "#212631", borderColor: "#212631", color: "white" }}
                  onClick={() => handleView(reviewRow)}
                >
                  <FontAwesomeIcon icon={faEye} />
                </CButton>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip id={`delete-${reviewRow.id}`}>{t(reviewT.action_tooltip.delete)}</Tooltip>}>
                <CButton color="danger" size="sm" onClick={() => handleDelete(reviewRow)}>
                  <FontAwesomeIcon icon={faTrash} />
                </CButton>
              </OverlayTrigger>
            </div>
          );
        },
      },
    ],
    [t]
  );

  return (
    <div className="reviews-list">
      <CRow className="mb-4">
        <CCol lg={12} className="user-list-filters d-flex gap-2 justify-content-end flex-wrap">
          <CFormInput
            placeholder={t(reviewT.search_placeholder)}
            type="text"
            value={tempSearchText}
            onChange={handleSearch}
            onKeyUp={handleSearch}
            className="filter-field-space form-control-sm same-input-cls"
            style={{ maxWidth: "260px", ...filterInputStyle }}
          />

          <CustomDropdown
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "200px", ...filterInputStyle }}
            value={freelancerUuid}
            onChange={handleFilterChange(setFreelancerUuid)}
            options={freelancerOptions}
            placeholder={t(reviewT.freelancer_filter_placeholder)}
          />

          <CustomDropdown
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "200px", ...filterInputStyle }}
            value={reviewerUuid}
            onChange={handleFilterChange(setReviewerUuid)}
            options={reviewerOptions}
            placeholder={t(reviewT.reviewer_filter_placeholder)}
          />

          <CustomDropdown
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "160px", ...filterInputStyle }}
            value={rating}
            onChange={handleFilterChange(setRating)}
            options={ratingOptions}
            placeholder={t(reviewT.rating_filter_placeholder)}
          />

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="reviewsStartDate" className="m-0 text-white">
              {t(reviewT.start_date_label)}
            </CFormLabel>
            <CFormInput
              type="date"
              id="reviewsStartDate"
              value={startDate}
              onChange={(e) => handleDateChange("start", e.target.value)}
              className="form-control same-input-cls"
              style={{ width: "155px", height: "45px", ...filterInputStyle }}
            />
          </div>

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="reviewsEndDate" className="m-0 text-white">
              {t(reviewT.end_date_label)}
            </CFormLabel>
            <CFormInput
              type="date"
              id="reviewsEndDate"
              value={endDate}
              onChange={(e) => handleDateChange("end", e.target.value)}
              min={startDate}
              className="form-control same-input-cls"
              style={{ width: "155px", height: "45px", ...filterInputStyle }}
            />
          </div>

          {(tempSearchText || freelancerUuid || reviewerUuid || rating || startDate || endDate) && (
            <CButton color="primary" type="button" onClick={handleClearFilter} className="filter-field-space" style={{ height: "45px", ...filterInputStyle }}>
              {t(reviewT.clear_filter_btn_text)}
            </CButton>
          )}
        </CCol>
      </CRow>

      <TNTable
        key={tableKey}
        columns={columns}
        data={reviews}
        paginationData={pagination}
        onSelectPage={(pageIndex) => {
          if (isFiltering.current) return;

          setCurrentPage(pageIndex + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        pageIndexGet={currentPage - 1}
        isLoading={loading}
        t={t}
        responsive={false}
        className="reviews-table"
      />
    </div>
  );
};

ReviewList.breadCrumbArray = [
  {
    name: breadcrumb_key.review?.list,
    pathname: reviewRoutes.list,
    active: true,
  },
];

export default ReviewList;
